import type { TenantCommercialRules } from '@console/types'
import { useEffect, useState } from 'react'
import { ApiError } from '../api.js'
import page from '../customer/CustomerPage.module.css'
import { SurfaceError, SurfaceLoading } from '../customer/SurfaceState.js'
import {
  useSaveTenantCommercialRules,
  useTenantCommercialRules,
} from '../tenant-commercial-rules/queries.js'
import { Button, Input, Toast } from '../ui/index.js'
import styles from './CustomerCommercialRulesPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-039` — customer console: commercial rules.
 *
 * Wire shape from `REQ-platform-core-the-commercial-rules-shape-for-
 * console`'s own answer: four fields, each a string or `null`, `PUT` always
 * replaces all four at once (no partial-patch shape exists), the three
 * percent fields are fractions of one on the wire (`"0.15"`, not `"15"`) —
 * shown and submitted as the same string the server owns the range rule
 * for, not converted client-side into a percentage the server never asked
 * for.
 *
 * **No conflict detection exists on this endpoint** (the REQ's own answer,
 * plainly) — two admins saving at once means the second write silently wins.
 * Said so on screen rather than implying a safety this form doesn't have.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only — what is sent is byte-for-byte what was sent
 * before. What changed:
 *
 * - **Zero pixels between fields.** Measured: 0, 0, 0. Every label sat flush
 *   against the input above it.
 * - **The unit convention was inside the label**, and `Input`'s label style
 *   uppercases, so the screen shouted "MARGIN FLOOR (FRACTION OF 1, E.G. 0.15
 *   FOR 15%)". A label names a field; `hint` explains it.
 * - **The server's refusal was printed verbatim under the Save button**,
 *   attached to no field and marking none as invalid, though `Input` has had
 *   an `error` state all along. It is still the server's own words — this maps
 *   them to a field, it does not rewrite them.
 * - **`Save` was the `secondary` variant** — the only action on the screen.
 * - The standing concurrency caveat sat *below* Save, so it was read after
 *   committing rather than before.
 *
 * **`REQ-design-the-customer-console-ux-audit` finding C1, now built**: a
 * sales manager thinks "15%", not "0.15". The three percent fields now
 * display and accept a percent (`15`); the wire's own fraction string
 * (`"0.15"`) is still what gets submitted and is still what the server owns
 * the range rule for — this form never re-derives that rule, it only
 * converts at the edge and echoes the exact string being sent, per 18UX's
 * own proposal ("keeps the server's string authoritative and keeps the
 * conversion inspectable"). String-based rounding (`toFixed` + trim), not
 * bare floating-point division, so `0.1 + 0.2`-class artifacts never reach
 * either the display or the wire.
 * ═══════════════════════════════════════════════════════════════════════════
 */

type FieldKey = keyof TenantCommercialRules

const PERCENT_FIELDS: ReadonlySet<FieldKey> = new Set([
  'marginFloorPercent',
  'discountFloorPercent',
  'defaultMarkupPercent',
])

function isPercentField(key: FieldKey): boolean {
  return PERCENT_FIELDS.has(key)
}

const FIELDS: { key: FieldKey; label: string; hint: string; placeholder: string }[] = [
  {
    key: 'creditLimitDefault',
    label: 'Default credit limit',
    hint: 'In AED. Applies to a customer with no limit of their own.',
    placeholder: 'e.g. 25000.00',
  },
  {
    key: 'marginFloorPercent',
    label: 'Margin floor',
    hint: 'As a percentage — enter 15 for 15%.',
    placeholder: 'e.g. 15',
  },
  {
    key: 'discountFloorPercent',
    label: 'Discount floor',
    hint: 'As a percentage — enter 10 for 10%.',
    placeholder: 'e.g. 10',
  },
  {
    key: 'defaultMarkupPercent',
    label: 'Default markup',
    hint: 'As a percentage — enter 20 for 20%.',
    placeholder: 'e.g. 20',
  },
]

/** Trims trailing zeros a `toFixed` call leaves behind, and a bare trailing `.` if that's all that's left. */
function trimTrailingZeros(value: string): string {
  if (!value.includes('.')) return value
  return value.replace(/0+$/, '').replace(/\.$/, '')
}

/** String-based, not `n * 100` — avoids floating-point artifacts reaching either the screen or the wire. */
function fractionToPercent(fraction: string): string {
  const n = Number(fraction)
  if (!Number.isFinite(n)) return fraction
  return trimTrailingZeros((n * 100).toFixed(6))
}

function percentToFraction(percent: string): string {
  const n = Number(percent)
  if (!Number.isFinite(n)) return percent
  return trimTrailingZeros((n / 100).toFixed(8))
}

/** `''` in the form means "clear this field" (submitted as `null`); the wire's own `null` shows as blank, never as the string `"null"`. Percent fields display the wire's fraction converted to a percent — see `fractionToPercent`. */
function toFormValue(key: FieldKey, value: string | null): string {
  if (value === null) return ''
  return isPercentField(key) ? fractionToPercent(value) : value
}

function toWireValue(key: FieldKey, value: string): string | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  return isPercentField(key) ? percentToFraction(trimmed) : trimmed
}

/**
 * Which field a server refusal is about, when it names one.
 *
 * Deliberately a match on the wire key the server itself used, not a guess: if
 * the message doesn't name a field, the message still shows — just at the form
 * level, exactly where it showed before. Nothing is swallowed either way.
 */
function fieldNamedIn(message: string): FieldKey | null {
  return FIELDS.map((field) => field.key).find((key) => message.includes(key)) ?? null
}

export function CustomerCommercialRulesPage() {
  const rules = useTenantCommercialRules()
  const save = useSaveTenantCommercialRules()
  const [form, setForm] = useState<Record<FieldKey, string> | null>(null)

  // Seed the form from the server's own value once it arrives — an admin's
  // in-progress edit must not be clobbered by a background refetch, so this
  // only runs when the form has nothing yet.
  useEffect(() => {
    if (rules.data !== undefined && form === null) {
      setForm({
        creditLimitDefault: toFormValue('creditLimitDefault', rules.data.creditLimitDefault),
        marginFloorPercent: toFormValue('marginFloorPercent', rules.data.marginFloorPercent),
        discountFloorPercent: toFormValue('discountFloorPercent', rules.data.discountFloorPercent),
        defaultMarkupPercent: toFormValue('defaultMarkupPercent', rules.data.defaultMarkupPercent),
      })
    }
  }, [rules.data, form])

  // `isError` checked BEFORE the `form === null` guard — `TST-001` finding 5.
  // `form` is only ever seeded from `rules.data`, which never arrives on a
  // failed load (a 403 for a non-admin, a network failure), so checking
  // `form === null` first left this screen on "Loading…" forever whenever
  // loading had already, definitively, failed.
  if (rules.isError) {
    return (
      <main className={page['page']}>
        <h1 className={page['title']}>Commercial rules</h1>
        <SurfaceError>
          {rules.error instanceof ApiError && rules.error.status === 403
            ? 'Commercial rules are admin-only, and this account is not an admin on it.'
            : 'The commercial rules could not be loaded.'}
        </SurfaceError>
      </main>
    )
  }

  if (rules.isPending || form === null) {
    return (
      <main className={page['page']}>
        <h1 className={page['title']}>Commercial rules</h1>
        <SurfaceLoading rows={4} />
      </main>
    )
  }

  const saveErrorMessage = save.isError
    ? save.error instanceof ApiError
      ? save.error.message
      : 'That could not be saved.'
    : null
  const erroredField = saveErrorMessage === null ? null : fieldNamedIn(saveErrorMessage)

  return (
    <main className={page['page']}>
      <h1 className={page['title']}>Commercial rules</h1>
      <p className={page['lede']}>
        These floors gate quote approval for your account. A blank field means undeclared, not zero.
      </p>

      <form
        className={styles['form']}
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate({
            creditLimitDefault: toWireValue('creditLimitDefault', form.creditLimitDefault),
            marginFloorPercent: toWireValue('marginFloorPercent', form.marginFloorPercent),
            discountFloorPercent: toWireValue('discountFloorPercent', form.discountFloorPercent),
            defaultMarkupPercent: toWireValue('defaultMarkupPercent', form.defaultMarkupPercent),
          })
        }}
      >
        {FIELDS.map((field) => {
          const trimmed = form[field.key].trim()
          // The live echo — S1's own "conversion inspectable" point — only
          // for a percent field with something in it; an empty or non-percent
          // field has nothing to echo.
          const echo =
            isPercentField(field.key) && trimmed !== ''
              ? `${field.hint} — sending "${percentToFraction(trimmed)}"`
              : field.hint
          return (
            <Input
              key={field.key}
              label={field.label}
              hint={echo}
              inputMode="decimal"
              placeholder={field.placeholder}
              value={form[field.key]}
              onChange={(event) => {
                setForm({ ...form, [field.key]: event.target.value })
              }}
              {...(erroredField === field.key && saveErrorMessage !== null
                ? { error: saveErrorMessage }
                : {})}
            />
          )
        })}

        <p className={styles['caveat']}>
          If someone else changes these at the same time, whichever save lands last wins silently —
          there is no conflict warning on this screen yet.
        </p>

        <div className={page['actions']}>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {saveErrorMessage !== null && erroredField === null && (
          <p role="alert" className={page['error']}>
            {saveErrorMessage}
          </p>
        )}
        {save.isSuccess && <Toast variant="ok">Saved.</Toast>}
      </form>
    </main>
  )
}
